import React, { useState, useEffect } from "react";
import {
  Button, FormGroup, Label, Input, Row, Col, Form, Spinner
} from "reactstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import { es } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import { AlumnoService } from "service/AlumnoService";
import FSelect from "components/Select/FSelect";

registerLocale("es", es);

const alumnoService = new AlumnoService();

const emptyMatricula = {
  ciclo: "",
  salon: "",
  fechadesde: null,
  fechahasta: null,
  tipomatricula: "P",
  montototal: "",
  cuotas: "1",
  modulos: [],
};

const FormMatricula = ({ selectedMatricula, alumnoId, listCiclos, listSalones, listModulos = [], onSave, onCancel }) => {
  const [matricula, setMatricula] = useState(emptyMatricula);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedMatricula) {
      setMatricula({
        ...selectedMatricula,
        ciclo: selectedMatricula.ciclo?._id || selectedMatricula.ciclo || "",
        salon: selectedMatricula.salon?._id || selectedMatricula.salon || "",
        fechadesde: selectedMatricula.fechadesde ? new Date(selectedMatricula.fechadesde) : null,
        fechahasta: selectedMatricula.fechahasta ? new Date(selectedMatricula.fechahasta) : null,
        modulos: (selectedMatricula.modulos || []).map(m => m.modulo?._id || m.modulo || m),
      });
    } else {
      setMatricula(emptyMatricula);
    }
  }, [selectedMatricula]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMatricula(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!matricula.ciclo || !matricula.salon) {
      alert("Ciclo y Salón son obligatorios");
      return;
    }
    if (!matricula.modulos || matricula.modulos.length === 0) {
      alert("Debe seleccionar al menos un módulo");
      return;
    }
    if (!matricula.fechadesde || !matricula.fechahasta) {
      alert("Las fechas de inicio y fin son obligatorias");
      return;
    }
    setLoading(true);
    try {
      const costomatriculado = parseFloat(matricula.montototal) || 0;
      const cantidadcuotas = parseInt(matricula.cuotas, 10) || 1;
      const costocuotas = cantidadcuotas > 0 ? costomatriculado / cantidadcuotas : 0;
      const dto = {
        ciclo: matricula.ciclo,
        salon: matricula.salon,
        tipomatricula: matricula.tipomatricula,
        alumno: alumnoId,
        fechadesde: matricula.fechadesde ? matricula.fechadesde.toISOString() : null,
        fechahasta: matricula.fechahasta ? matricula.fechahasta.toISOString() : null,
        costomatriculado,
        cantidadcuotas,
        costocuotas,
        modulos: matricula.modulos.map(id => ({ modulo: id })),
        ...(matricula._id ? { _id: matricula._id } : {}),
      };
      let res;
      if (matricula._id) {
        res = await alumnoService.updateMatricula(dto);
      } else {
        res = await alumnoService.addMatricula(dto);
      }
      if (res?.confirmation === "success") {
        if (onSave) onSave();
      } else {
        alert(res?.message || "Error al guardar la matrícula");
      }
    } catch (error) {
      alert("Error al guardar la matrícula");
    } finally {
      setLoading(false);
    }
  };

  const isEditing = !!matricula._id;

  return (
    <Form>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Ciclo *</Label>
            <FSelect
              name="ciclo"
              value={matricula.ciclo}
              onChange={handleChange}
              options={listCiclos.map(c => ({ value: c._id, label: c.denominacion }))}
              placeholder="Seleccione Ciclo"
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label>Salón *</Label>
            <FSelect
              name="salon"
              value={matricula.salon}
              onChange={handleChange}
              options={listSalones.map(s => ({ value: s._id, label: s.denominacion }))}
              placeholder="Seleccione Salón"
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup>
            <Label>Fecha Inicio</Label>
            <DatePicker
              selected={matricula.fechadesde}
              onChange={(date) => setMatricula(prev => ({ ...prev, fechadesde: date }))}
              dateFormat="dd/MM/yyyy"
              locale="es"
              placeholderText="dd/mm/aaaa"
              className="form-control"
              wrapperClassName="w-100"
              isClearable
              portalId="datepicker-portal"
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label>Fecha Fin</Label>
            <DatePicker
              selected={matricula.fechahasta}
              onChange={(date) => setMatricula(prev => ({ ...prev, fechahasta: date }))}
              dateFormat="dd/MM/yyyy"
              locale="es"
              placeholderText="dd/mm/aaaa"
              className="form-control"
              portalId="datepicker-portal"
              wrapperClassName="w-100"
              minDate={matricula.fechadesde}
              isClearable
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="4">
          <FormGroup>
            <Label>Monto Total (S/)</Label>
            <Input
              type="number"
              name="montototal"
              value={matricula.montototal}
              onChange={handleChange}
              placeholder="0.00"
              min="0"
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label>N° Cuotas</Label>
            <Input
              type="number"
              name="cuotas"
              value={matricula.cuotas}
              onChange={handleChange}
              min="1"
            />
          </FormGroup>
        </Col>
        <Col md="4">
          <FormGroup>
            <Label>Tipo</Label>
            <FSelect
              name="tipomatricula"
              value={matricula.tipomatricula}
              onChange={handleChange}
              options={[
                { value: "P", label: "Presencial" },
                { value: "V", label: "Virtual" },
              ]}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="12">
          <FormGroup>
            <Label>Módulos *</Label>
            <FSelect
              name="modulos"
              isMulti
              value={Array.isArray(matricula.modulos) ? matricula.modulos : []}
              onChange={handleChange}
              options={listModulos.map(m => ({ value: m._id, label: `${m.titulo} — ${m.denominacion}` }))}
              placeholder="Seleccione módulos"
            />
          </FormGroup>
        </Col>
      </Row>
      <div className="d-flex justify-content-end" style={{ gap: "8px" }}>
        <Button color="secondary" size="sm" onClick={onCancel}>
          Cancelar
        </Button>
        <Button color="primary" size="sm" onClick={handleSave} disabled={loading}>
          {loading ? <Spinner size="sm" /> : (isEditing ? "Actualizar" : "Registrar")}
        </Button>
      </div>
    </Form>
  );
};

export default FormMatricula;
